/**
 * GoogleMap Component
 *
 * A wrapper component that uses the google-maps module to render
 * a Google Map with markers/overlays.
 */
import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import {
	GoogleMap as GoogleMapBase,
	Marker,
	MapPin,
	Overlay
} from "components/google-maps";
import { GTM_CLASSNAMES } from "config/gtmSelectors";

import "./GoogleMap.scss";

function GoogleMap({
	center,
	locations = [],
	radius = 15,
	onMove,
	onLoad,
	onMapReady,
	markerType = "default",
	renderOverlay,
	className,
	children,
	searchFromMove,
	setSearchFromMove,
	selectedLocationId,
	hoveredLocationId,
	onLocationClick,
	onLocationHover,
	ariaLabel
}) {
	const renderMarkers = () => {
		if (!locations || locations.length === 0) {
			return null;
		}

		return locations.map((loc) => {
			const key = loc.id || `${loc.latitude}-${loc.longitude}`;
			const isSelected = selectedLocationId === loc.id;
			const isHovered = hoveredLocationId === loc.id;
			// Highlight if selected (clicked) OR hovered - both can be active at the same time
			const isHighlighted = isSelected || isHovered;

			if (markerType === "pin") {
				return (
					<MapPin
						key={key}
						location={loc}
						image={loc.image}
					/>
				);
			}

			if (markerType === "overlay") {
				return (
					<Overlay
						key={key}
						location={loc}
						selected={isHighlighted}
						className={classnames(GTM_CLASSNAMES.SEARCH_MAP_PIN, {
							selected: isHighlighted
						})}
						label={
							renderOverlay
								? renderOverlay(loc, isHighlighted)
								: loc.label
						}
						onClick={onLocationClick}
						onHover={onLocationHover}
					/>
				);
			}

			// Default marker
			return (
				<Marker
					key={key}
					location={loc}
					icon={loc.icon}
					label={loc.label}
				/>
			);
		});
	};

	return (
		<div className={`google-map-wrapper ${className || ""}`}>
			<GoogleMapBase
				center={center}
				locationBounds={locations}
				radius={radius}
				onMove={onMove}
				onLoad={onLoad}
				onMapReady={onMapReady}
				searchFromMove={searchFromMove}
				setSearchFromMove={setSearchFromMove}
				ariaLabel={ariaLabel}
			>
				{renderMarkers()}
				{children}
			</GoogleMapBase>
		</div>
	);
}

GoogleMap.propTypes = {
	center: PropTypes.shape({
		latitude: PropTypes.number.isRequired,
		longitude: PropTypes.number.isRequired
	}).isRequired,
	locations: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			latitude: PropTypes.number.isRequired,
			longitude: PropTypes.number.isRequired,
			label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
		})
	),
	radius: PropTypes.number,
	onMove: PropTypes.func,
	onLoad: PropTypes.func,
	markerType: PropTypes.oneOf(["default", "pin", "overlay"]),
	renderOverlay: PropTypes.func,
	className: PropTypes.string,
	children: PropTypes.node,
	searchFromMove: PropTypes.bool,
	setSearchFromMove: PropTypes.func,
	selectedLocationId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	hoveredLocationId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	onLocationClick: PropTypes.func,
	onLocationHover: PropTypes.func,
	onMapReady: PropTypes.func,
	ariaLabel: PropTypes.string
};

export default GoogleMap;
